import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api, { getCourses, getTeachers, getSubjects, getPublicStats, getMyEnrollments } from '../services/api';
import CourseCard from '../components/CourseCard';
import CampaignBanner from '../components/CampaignBanner';
import { getActiveCampaigns } from '../services/api';
import { useScrollReveal, animateCounter } from '../hooks/useScrollReveal';
import useAuthStore from '../context/authStore';
import './HomePage.css';

const getDynamicFaqs = (commission) => [
  { q: 'كيف أبدأ كمدرّس على المنصة؟',               a: 'سجّل حساب كمدرّس، أنشئ صفحتك الشخصية، ثم ارفع أول كورس أو جدول حصة لايف. المنصة بتتكفل بكل التفاصيل التقنية من استضافة الفيديو لاستقبال المدفوعات.' },
  { q: 'امتى بقبض أرباحي كمدرّس؟',                  a: 'أرباحك بتتراكم تلقائياً وتقدر تطلب تحويلها أسبوعياً. بتاخد ' + (100 - commission) + '% من كل اشتراك بدون أي خصومات تانية.' },
  { q: 'هل الطالب يقدر يشترك في حصة واحدة؟',        a: 'أيوه، المنصة بتدعم الاشتراك في حصص فردية أو كورسات كاملة — المدرّس هو اللي بيحدد السعر لكل حصة أو للكورس الكامل.' },
  { q: 'إيه طرق الدفع المتاحة؟',                     a: 'المنصة بتدعم Paymob — فيزا، ماستركارد، فودافون كاش، اتصالات كاش، وكل طرق الدفع المصرية.' },
  { q: 'هل فيه درس تجريبي مجاني؟',                   a: 'أيوه، أول درس في كل كورس متاح مجاناً لكل الطلاب قبل ما يقرروا يشتركوا.' },
  { q: 'المنصة بتدعم أنواع مختلفة من المحتوى؟',       a: 'أيوه، بتدعم فيديوهات مسجلة، حصص لايف، ملخصات PDF، واختبارات تفاعلية.' },
];

export default function HomePage() {
  const { user, userDoc } = useAuthStore();
  const [courses,    setCourses]    = useState([]);
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [teachers,   setTeachers]   = useState([]);
  const [subjects,   setSubjects]   = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [lastPurchase, setLastPurchase] = useState(null);
  const [openFaq,    setOpenFaq]    = useState(null);
  const [liveStats,  setLiveStats]  = useState({ teachers:0, courses:0, enrollments:0, avgRating:0, totalReviews: 0, commission: 15 }); // null = all closed
  const [publicReviews, setPublicReviews] = useState([]);

  // Scroll-reveal refs
  const pageRef = useScrollReveal();

  // Counter refs
  const counterRefs = React.useRef([]);

  useEffect(() => {
    // Fetch latest published courses (prioritize student grade if logged in)
    const courseParams = { limit: 6 };
    if (userDoc?.grade) courseParams.grade = userDoc.grade;

    getCourses(courseParams)
      .then(r => {
        let list = r.data.courses || [];
        // If no courses found for specific grade, fallback to general latest courses
        if (list.length === 0 && courseParams.grade) {
          getCourses({ limit: 6 }).then(r2 => setCourses(r2.data.courses || []));
        } else {
          setCourses(list);
        }
      })
      .catch(() => {});

    getPublicStats()
      .then(r => setLiveStats(r.data))
      .catch(() => {});

    getSubjects()
      .then(r => setSubjects(r.data.subjects || []))
      .catch(() => {});

    // Fetch approved teachers
    getTeachers({ limit: 8 })
      .then(r => setTeachers(r.data.teachers || []))
      .catch(() => {});

    getActiveCampaigns()
      .then(r => setActiveCampaigns(r.data || []))
      .catch(() => {});

    api.get('/admin/public-reviews')
      .then(r => setPublicReviews(r.data || []))
      .catch(() => {});

    if (user && userDoc?.role === 'student') {
      getMyEnrollments()
        .then(r => {
          const sorted = (r.data || [])
            .filter(e => e.progress < 100)
            .sort((a, b) => new Date(b.updatedAt || b.enrolledAt) - new Date(a.updatedAt || a.enrolledAt))
            .slice(0, 3);
          setMyEnrollments(sorted);
        })
        .catch(() => {});
    }

    // Real "Just Purchased" notifications from recent enrollments
    const fetchRecentPurchases = () => {
      api.get('/admin/recent-enrollments')
        .then(r => {
          const list = r.data || [];
          if (list.length > 0) {
            const pick = list[Math.floor(Math.random() * list.length)];
            setLastPurchase({ name: pick.studentName, course: pick.courseTitle });
            setTimeout(() => setLastPurchase(null), 6000);
          }
        })
        .catch(() => {});
    };

    const timer = setInterval(() => {
      if (Math.random() > 0.6) fetchRecentPurchases();
    }, 20000);

    return () => clearInterval(timer);
  }, []);

  // Counter animation — re-runs when liveStats loads from API
  useEffect(() => {
    const STATS_DEF = [
      { value: liveStats.teachers,    suffix: '+' },
      { value: liveStats.courses,     suffix: '+' },
      { value: liveStats.enrollments, suffix: '+' },
      { value: liveStats.avgRating,   suffix: '', isFloat: true },
    ];
    const counters = counterRefs.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el  = entry.target;
            const idx = parseInt(el.dataset.idx);
            const s   = STATS_DEF[idx];
            if (s) animateCounter(el, s.value, 1600, s.suffix);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.5 }
    );
    counters.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [liveStats]);

  const toggleFaq = (i) => setOpenFaq(prev => prev === i ? null : i);

  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));
  const satisfaction = liveStats.avgRating ? Math.min(100, Math.round((liveStats.avgRating / 5) * 100 + 2)) : 98;
  const realStudentCountStr = liveStats.students > 1000 ? `${(liveStats.students / 1000).toFixed(1)}k` : liveStats.students;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="homepage page-enter"
      ref={pageRef}
      id="homepage-root"
    >

      {/* ════ VIRAL GROWTH BANNER ════ */}
      {userDoc?.role === 'student' && (
        <section className="viral-referral-banner" style={{ background: 'linear-gradient(135deg, #0d2a52 0%, #1a9bd5 100%)', color: '#fff', padding: '24px 0' }}>
            <div className="sec-inner">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
                    <div>
                        <h2 style={{ fontSize: 20, margin: 0 }}>🎁 شارك واكسب رصيد مجاني!</h2>
                        <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 14 }}>ادعُ أصدقاءك للتسجيل في مدرسك واحصل على 10 ج.م رصيد عن كل صديق.</p>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ background: 'rgba(255,255,255,0.1)', padding: '8px 16px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <code style={{ fontSize: 16, fontWeight: 800 }}>{userDoc?.referralCode}</code>
                            <button
                                onClick={() => {
                                    const link = `${window.location.origin}/register?ref=${userDoc?.referralCode}`;
                                    navigator.clipboard.writeText(link);
                                    toast.success('تم نسخ رابط الإحالة!');
                                }}
                                style={{ background: '#fff', color: '#0d2a52', border: 'none', padding: '4px 12px', borderRadius: 4, fontWeight: 700, cursor: 'pointer' }}
                            >نسخ الرابط</button>
                        </div>
                        <Link to="/dashboard" className="btn btn-outline btn-sm" style={{ borderColor: '#fff', color: '#fff' }}>رصيدي ←</Link>
                    </div>
                </div>
            </div>
        </section>
      )}

      {/* ════ CONTINUE WATCHING ════ */}
      {userDoc?.role === 'student' && myEnrollments.length > 0 && (
        <section className="continue-watching-section" style={{ background: 'var(--bg-light)', padding: '40px 0' }}>
          <div className="sec-inner">
            <div className="flex-between mb-24">
              <div>
                <h2 className="section-title" style={{ fontSize: 24 }}>📺 كمل اللي بدأته</h2>
                <p className="section-sub">أحدث الكورسات اللي كنت بتشوفها</p>
              </div>
              <Link to="/dashboard" className="btn btn-ghost">كل كورساتي ←</Link>
            </div>
            <div className="grid-3">
              {myEnrollments.map((e, i) => (
                <Link key={e.id || i} to={`/courses/${e.courseSlug || e.courseId}`} className="card continue-card card-anim" style={{ padding: 0, overflow: 'hidden', textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ height: 120, position: 'relative' }}>
                    {e.courseThumbnail ? (
                      <img src={e.courseThumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>📚</div>
                    )}
                    <div className="progress-bar" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, borderRadius: 0 }}>
                      <div className="progress-fill" style={{ width: `${e.progress}%` }} />
                    </div>
                  </div>
                  <div style={{ padding: 15 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5 }} className="text-truncate">{e.courseTitle}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>وصلت لـ {e.progress}%</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ════ HERO ════ */}
      <section className="hero-section">
        {activeCampaigns.length > 0 && (
          <div className="homepage-campaign-wrap" data-reveal="down">
            <CampaignBanner campaign={activeCampaigns[0]} />
          </div>
        )}
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-pill" data-reveal="down" data-delay="100">
              <span className="hero-dot badge-pulse" />
              منصة تعليمية مصرية مستقلة
            </div>
            <h1 data-reveal="up" data-delay="150">
              <span className="hero-blue">مدرسك</span><br />
              علّم <span className="hero-blue">أذكى</span>... واكسب <span className="hero-teal">أكتر</span>
            </h1>
            <p className="hero-tagline" data-reveal="up" data-delay="200">حوّل علمك لدخل — من غير موقع ولا تعقيد تقني</p>
            <p className="hero-desc" data-reveal="up" data-delay="250">
              مدرسك بتساعد المدرسين المستقلين يعرضوا كورساتهم، يرفعوا فيديوهاتهم، يعملوا حصص لايف، ويستقبلوا مدفوعات الطلاب — كل ده في منصة واحدة.
            </p>
            <div className="hero-features" data-reveal="up" data-delay="300">
              {[
                { icon: '🎬', label: 'كورسات مسجلة وحصص لايف' },
                { icon: '💳', label: 'مدفوعات إلكترونية آمنة' },
                { icon: '📊', label: 'لوحة تحكم وتحليلات' },
                { icon: '⭐', label: 'تقييمات ونظام إحالات' },
              ].map(f => (
                <div key={f.label} className="hfeat">
                  <span className="hfeat-icon icon-bounce">{f.icon}</span>{f.label}
                </div>
              ))}
            </div>
            <div className="hero-btns" data-reveal="up" data-delay="350">
              <Link to="/register?role=teacher" className="btn btn-primary btn-lg">← انضم كمدرّس الآن</Link>
              <Link to="/courses" className="btn btn-outline btn-lg">▶ تصفح الكورسات</Link>
            </div>
          </div>

          <div className="hero-dashboard hero-float" data-reveal="left" data-delay="200">
            <div className="dash-header">
              <div>
                <div className="dash-title">لوحة تحكم المدرّس</div>
                <div className="dash-sub">كورساتك وطلابك وأرباحك في مكان واحد</div>
              </div>
              <span className="badge badge-green">{100 - (liveStats.commission || 15)}% لك</span>
            </div>
            <div className="dash-body">
              <div className="dash-stats-grid">
                <div className="dstat"><div className="dstat-num">{liveStats.enrollments}</div><div className="dstat-lbl">طالب مشترك</div></div>
                <div className="dstat"><div className="dstat-num">{liveStats.courses}</div><div className="dstat-lbl">كورس منشور</div></div>
              </div>
              <div className="dash-courses">
                {courses.length > 0 ? courses.slice(0, 3).map((c, i) => (
                  <div key={i} className="dcourse">
                    <span className={`dc-dot ${['red', 'blue', 'teal'][i % 3]}`} />
                    {c.title.slice(0, 25)}{c.title.length > 25 ? '...' : ''}
                    <span className={`badge ${c.isLive ? 'badge-red' : 'badge-green'}`} style={{ fontSize: 10, marginRight: 'auto' }}>
                      {c.isLive ? '🔴 لايف' : '✓ متاح'}
                    </span>
                  </div>
                )) : (
                  ['رياضيات ثانوي', 'فيزياء تانية ثانوي', 'كيمياء حديثة'].map((t, i) => (
                    <div key={i} className="dcourse">
                      <span className={`dc-dot ${['red', 'blue', 'teal'][i]}`} />
                      {t}
                      <span className="badge badge-green" style={{ fontSize: 10, marginRight: 'auto' }}>✓ متاح</span>
                    </div>
                  ))
                )}
              </div>
              <div className="dash-earn">
                <div>
                  <div className="earn-lbl">منصة متكاملة</div>
                  <div className="earn-val">أرباحك {100 - (liveStats.commission || 15)}%</div>
                </div>
                <Link to="/dashboard" className="btn btn-primary btn-sm">ابدأ الآن</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ TESTIMONIALS ════ */}
      <section className="testimonials-section" data-reveal="up">
        <div className="sec-inner">
          <div className="sec-head text-center">
            <h2 className="section-title section-title-anim">آراء طلابنا ومدرسينا</h2>
            <p className="section-sub">قصص نجاح من داخل مجتمع مدرسك</p>
          </div>

          {publicReviews.length > 0 ? (
            <div className="grid-3">
              {publicReviews.slice(0, 6).map((r, i) => (
                <div key={r.id || i} className="card testimonial-card card-anim" style={{ animationDelay: `${i * 100}ms` }}>
                  <div className="testi-header">
                    <div className="testi-avatar-wrap">
                      {(r.userAvatar || r.studentAvatar) ? (
                        <img src={r.userAvatar || r.studentAvatar} alt="" className="testi-avatar" />
                      ) : (
                        <div className="testi-avatar-ph">{(r.userName || r.studentName)?.[0] || '؟'}</div>
                      )}
                    </div>
                    <div>
                      <div className="testi-name">{r.userName || r.studentName || 'طالب مجهول'}</div>
                      <div className="testi-role">{r.userRole === 'teacher' ? 'مدرّس على المنصة' : 'طالب على المنصة'}</div>
                    </div>
                  </div>
                  <p className="testi-text">"{r.comment || 'تجربة تعليمية ممتازة جداً ومحتوى هادف.'}"</p>
                  <div className="testi-stars">{stars(r.rating)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-reviews-placeholder text-center" style={{ padding: '40px 20px', background: 'rgba(255,255,255,0.05)', borderRadius: 12 }}>
              <div style={{ fontSize: 40, marginBottom: 15 }}>✨</div>
              <p style={{ color: 'var(--text-muted)' }}>كن أول من يشارك تجربته مع منصة مدرسك!</p>
            </div>
          )}
        </div>
      </section>

      {/* ════ STATS BAR ════ */}
      <section className="stats-bar">
        <div className="stats-bar-inner">
          {[
            { icon: '👨‍🏫', label: 'مدرّس متخصص', value: liveStats.teachers,    suffix: '+' },
            { icon: '📚',   label: 'كورس متاح',    value: liveStats.courses,     suffix: '+' },
            { icon: '👨‍🎓', label: 'طالب نشط',     value: liveStats.enrollments, suffix: '+' },
            { icon: '⭐',   label: 'تقييم متوسط',  value: liveStats.avgRating,   suffix: '', isFloat: true },
          ].map((s, i) => (
            <div key={s.label} className="stat-bar-item" data-reveal="up" data-delay={`${100 + i * 80}`}>
              <span className="stat-bar-icon icon-bounce">{s.icon}</span>
              <span
                className="stat-bar-num stat-num-glow count-up"
                ref={el => counterRefs.current[i] = el}
                data-idx={i}
              >
                {s.isFloat ? s.value : '0'}{s.suffix}
              </span>
              <span className="stat-bar-lbl">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ════ SEARCH ════ */}
      <section className="search-section">
        <div data-reveal="up" data-delay="100">
          <h2 className="section-title text-center mb-8 section-title-anim">ابحث عن مدرّسك</h2>
          <p className="section-sub text-center mb-24">اختار المادة والمرحلة الدراسية وابدأ التعلم مع أفضل المدرسين</p>
        </div>
        <div className="search-box" data-reveal="up" data-delay="200">
          <div className="search-row">
            <input className="form-input" placeholder="🔍  المادة أو اسم المدرّس" />
            <input className="form-input" placeholder="📚  المرحلة الدراسية" />
            <Link to="/courses" className="btn btn-primary">بحث</Link>
          </div>
          <div className="subject-tags">
            {subjects.slice(0, 10).map((s, i) => (
              <Link key={s} to={`/courses?subject=${s}`} className="subject-tag" style={{ transitionDelay: `${i * 40}ms` }}>{s}</Link>
            ))}
            <span className="tag-label">:المواد الشائعة</span>
          </div>
        </div>
      </section>

      {/* ════ ABOUT CARDS ════ */}
      <section className="about-section" id="features">
        <div className="sec-inner">
          <div className="sec-head text-center" data-reveal="up">
            <h2 className="section-title section-title-anim">عن منصة مدرسك</h2>
            <p className="section-sub">منصة متكاملة تربط المدرسين المستقلين بالطلاب</p>
          </div>
          <div className="grid-4">
            {[
              { icon: '🏠', title: 'صفحة شخصية للمدرّس', desc: 'كل مدرّس عنده صفحة شخصية بكل كورساته وتقييماته.' },
              { icon: '🎬', title: 'فيديوهات + حصص لايف', desc: 'ارفع فيديوهاتك المسجلة أو جدول حصص لايف مباشرة.' },
              { icon: '💳', title: 'دفع إلكتروني آمن',    desc: 'Paymob — فيزا، ماستركارد، فودافون كاش وأكثر.' },
              { icon: '📊', title: 'تحليلات ومتابعة',     desc: 'تابع أرباحك وعدد طلابك وأداء كل كورس.' },
            ].map((f, i) => (
              <div key={f.title} className="card about-card card-anim card-border-anim card-shimmer" data-reveal="up" data-delay={`${100 + i * 80}`}>
                <div className="about-icon icon-bounce">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ HOW IT WORKS ════ */}
      <section className="how-section">
        <div className="sec-inner">
          <div className="sec-head text-center" data-reveal="up">
            <h2 className="section-title section-title-anim">كيف تعمل المنصة؟</h2>
            <p className="section-sub">خطوات بسيطة سواء كنت طالب أو مدرّس</p>
          </div>

          <div className="how-tab-label blue-label" data-reveal="fade" data-delay="100">تجربة الطالب</div>
          <p className="how-tab-sub" data-reveal="fade" data-delay="150">اشترك في كورس واحد أو حصة منفردة وابدأ التعلم فوراً</p>
          <div className="grid-3 mb-24">
            {[
              { n: 1, icon: '🔍', t: 'دوّر على مدرّسك',   d: 'ابحث بالمادة أو المرحلة واختار المدرّس المناسب' },
              { n: 2, icon: '💳', t: 'اشترك وادفع بأمان', d: 'ادفع حصة واحدة أو كورس كامل عبر Paymob بأمان 100%' },
              { n: 3, icon: '🎓', t: 'تعلّم وتابع تقدمك', d: 'شاهد الفيديوهات واحضر اللايف في أي وقت' },
            ].map((s, i) => (
              <div key={s.n} className="card step-card card-anim card-shimmer" data-reveal="up" data-delay={`${200 + i * 100}`}>
                <div className="step-num">{s.n}</div>
                <div className="step-icon blue-ico icon-bounce">{s.icon}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>

          <div className="teacher-box" data-reveal="up" data-delay="100">
            <div className="how-tab-label teal-label">تجربة المدرّس</div>
            <p className="how-tab-sub">أنشئ صفحتك وارفع كورساتك وابدأ تكسب من أول يوم</p>
            <div className="grid-4 mb-24">
              {[
                { n: 1, icon: '📝', t: 'أنشئ حسابك',    d: 'سجّل كمدرّس وابني صفحتك الشخصية' },
                { n: 2, icon: '🎬', t: 'ارفع كورساتك',  d: 'ارفع فيديوهاتك والكلاود بيتكفل بالباقي' },
                { n: 3, icon: '👥', t: 'استقبل الطلاب', d: 'المنصة بتجمع المدفوعات وبتحولها ليك' },
                { n: 4, icon: '💰', t: `اقبض ${100 - (liveStats.commission || 15)}%`, d: `${100 - (liveStats.commission || 15)}% ليك مباشرة — بدون مصاريف خفية` },
              ].map((s, i) => (
                <div key={s.n} className="mini-step card-anim" data-reveal="up" data-delay={`${150 + i * 70}`}>
                  <div className="mini-num">{s.n}</div>
                  <div className="mini-icon icon-bounce">{s.icon}</div>
                  <h3>{s.t}</h3>
                  <p>{s.d}</p>
                </div>
              ))}
            </div>
            <div className="text-center" data-reveal="up" data-delay="400">
              <Link to="/register?role=teacher" className="btn btn-primary btn-lg">👨‍🏫 انضم كمدرّس الآن مجاناً</Link>
            </div>
          </div>

          <div className="profit-strip" data-reveal="scale" data-delay="100">
            <div className="profit-nums">
              <div className="pnum"><div className="pn-big">{100 - (liveStats.commission || 15)}%</div><div className="pn-lbl">للمدرّس</div></div>
              <div className="pdiv" />
              <div className="pnum"><div className="pn-big">{liveStats.commission || 15}%</div><div className="pn-lbl">للمنصة</div></div>
            </div>
            <p className="profit-text">نموذج ربح شفاف وعادل — المنصة بتاخد {liveStats.commission || 15}% بس لتغطية السيرفر، بوابة الدفع، التسويق، والدعم الفني.</p>
          </div>
        </div>
      </section>

      {/* ════ SOCIAL PROOF ════ */}
      <section className="social-proof-section" data-reveal="up">
        <div className="sec-inner">
          <div className="proof-grid">
            <div className="proof-text">
              <h2 className="section-title-anim">انضم لمجتمع <span className="hero-blue">مدرسك</span></h2>
              <p>أكثر من {liveStats.students || 0} طالب وطالبة بيتعلموا يومياً على المنصة</p>
              <div className="proof-avatars">
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} className="p-avatar" style={{ transform: `translateX(${i * 15}px)`, zIndex: 10 - i }}>
                    <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="" />
                  </div>
                ))}
                <span className="p-stat-main-lbl">+{realStudentCountStr || 0} طالب</span>
              </div>
            </div>
            <div className="proof-stats">
              <div className="p-stat-box">
                <div className="p-stat-val">{satisfaction}%</div>
                <div className="p-stat-lbl">رضا الطلاب</div>
              </div>
              <div className="p-stat-box">
                <div className="p-stat-val">24/7</div>
                <div className="p-stat-lbl">دعم فني</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════ FEATURED COURSES — always shown ════ */}
      <section className="featured-section">
        <div className="sec-inner">
          <div className="flex-between mb-24">
            <div data-reveal="right">
              <h2 className="section-title section-title-anim">
                {userDoc?.grade ? `ترشيحات لـ ${userDoc.grade}` : 'كورسات مميزة'}
              </h2>
              <p className="section-sub">
                {userDoc?.grade ? 'أفضل الكورسات المختارة لمرحلتك الدراسية' : 'اختار من أفضل الكورسات المتاحة على المنصة'}
              </p>
            </div>
            <Link to="/courses" className="btn btn-outline" data-reveal="left">عرض الكل</Link>
          </div>

          {courses.length > 0 ? (
            <div className="grid-3">
              {courses.map((c, i) => (
                <div key={c.id} className="course-card-anim" style={{ animationDelay: `${i * 70}ms` }}>
                  <CourseCard course={c} />
                </div>
              ))}
            </div>
          ) : (
            /* Placeholder when no published courses yet */
            <div className="no-courses-placeholder" data-reveal="up">
              <div className="ncp-grid">
                {[1,2,3].map(n => (
                  <div key={n} className="ncp-card">
                    <div className="ncp-thumb" />
                    <div className="ncp-body">
                      <div className="ncp-line ncp-line-sm" />
                      <div className="ncp-line ncp-line-lg" />
                      <div className="ncp-line ncp-line-md" />
                    </div>
                  </div>
                ))}
              </div>
              <p className="ncp-msg">لا توجد كورسات منشورة بعد — كن أول من ينشر كورس! 🚀</p>
              <Link to="/register?role=teacher" className="btn btn-primary">انضم كمدرّس</Link>
            </div>
          )}
        </div>
      </section>

      {/* ════ TEACHERS — always shown ════ */}
      <section className="teachers-section">
        <div className="sec-inner">
          <div className="flex-between mb-24">
            <div data-reveal="right">
              <h2 className="section-title section-title-anim">مدرسونا</h2>
              <p className="section-sub">تعرف على أفضل المدرسين المستقلين على المنصة</p>
            </div>
            <Link to="/teachers" className="btn btn-outline" data-reveal="left">عرض الكل</Link>
          </div>

          {/* Featured Teachers Highlight */}
          {teachers.some(t => t.rating >= 4.8) && (
            <div className="featured-teacher-highlight card card-anim mb-24" style={{ background: 'linear-gradient(135deg, #0d2a52 0%, #1a9bd5 100%)', color: '#fff', border: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: 20 }}>
                <div style={{ fontSize: 40 }}>🏆</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18 }}>أعلى المدرسين تقييماً هذا الشهر</h3>
                  <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>نخبة من المدرسين المتميزين بمحتواهم وتفاعلهم مع الطلاب</p>
                </div>
              </div>
            </div>
          )}

          {teachers.length > 0 ? (
            <div className="teachers-home-grid">
              {teachers.slice(0, 4).map((t, i) => (
                <Link
                  key={t.uid}
                  to={`/teachers/${t.username || t.uid}`}
                  className="teacher-home-card card card-anim card-border-anim"
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <div className="thc-avatar-wrap">
                    {t.avatar
                      ? <img src={t.avatar} alt={t.name} className="thc-avatar-img" />
                      : <div className="thc-avatar-ph">{t.name?.[0] || '؟'}</div>
                    }
                  </div>
                  <h3 className="thc-name">{t.name}</h3>
                  {t.title && <div className="thc-title" style={{ fontSize: 12, color: 'var(--blue)', marginBottom: 4 }}>{t.title}</div>}
                  {t.rating > 0 && (
                    <div className="thc-rating">
                      <span className="stars" style={{ fontSize: 13 }}>
                        {stars(t.rating)}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({t.totalReviews || 0})</span>
                    </div>
                  )}
                  <div className="thc-subjects">
                    {t.subjects?.slice(0, 2).map(s => (
                      <span key={s} className="badge badge-blue" style={{ fontSize: 10 }}>{s}</span>
                    ))}
                  </div>

                  <div className="tc-mini-stats" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, fontSize: 11, color: 'var(--text-muted)', justifyContent: 'center' }}>
                    <span>📚 {t.courseCount || 0} كورس</span>
                    <span>👥 {t.totalEnrolled || 0} طالب</span>
                  </div>

                  <span className="thc-cta" style={{ marginTop: 12 }}>عرض الملف ←</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="no-courses-placeholder" data-reveal="up">
              <div className="ncp-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
                {[1,2,3,4].map(n => (
                  <div key={n} className="ncp-card" style={{ textAlign: 'center', padding: 20 }}>
                    <div className="ncp-avatar-ph" />
                    <div className="ncp-line ncp-line-md" style={{ margin: '10px auto 6px' }} />
                    <div className="ncp-line ncp-line-sm" style={{ margin: '0 auto' }} />
                  </div>
                ))}
              </div>
              <p className="ncp-msg">لا يوجد مدرسون مسجلون بعد — كن أول مدرّس! 🎓</p>
              <Link to="/register?role=teacher" className="btn btn-primary">انضم كمدرّس</Link>
            </div>
          )}
        </div>
      </section>

      {/* ════ WHY ════ */}
      <section className="why-section">
        <div className="sec-inner">
          <div className="sec-head text-center" data-reveal="up">
            <h2 className="section-title section-title-anim">لماذا تختار مدرسك؟</h2>
            <p className="section-sub">حللنا المشاكل الحقيقية للمدرسين المستقلين</p>
          </div>
          <div className="grid-4 mb-32">
            {[
              { icon: '🚫', title: 'بدون موقع مكلف',  desc: 'صفحتك الشخصية جاهزة في دقائق بدون أي تكلفة' },
              { icon: '💳', title: 'دفع إلكتروني جاهز', desc: 'استقبل مدفوعات بدون أي إعداد تقني من جهتك' },
              { icon: '☁️', title: 'رفع فيديوهات سهل', desc: 'ارفع كورساتك على الكلاود بضغطة زر' },
              { icon: '📈', title: 'تحليلات ذكية',     desc: 'شوف تقدم طلابك وأرباحك من لوحة تحكم واحدة' },
            ].map((c, i) => (
              <div key={c.title} className="why-card card-anim card-border-anim" data-reveal="up" data-delay={`${100 + i * 80}`}>
                <div className="why-icon icon-bounce">{c.icon}</div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="more-strip" data-reveal="up" data-delay="150">
            <div>
              <h3>وأكثر من ذلك...</h3>
              <p>مميزات بتخليك تركز على التدريس بس</p>
              <div className="more-tags">
                {['🎁 أول درس مجاني', '👥 نظام إحالات', '⭐ مدرّس الأسبوع', '🔔 إشعارات تذكير', '📝 اختبارات تفاعلية'].map(t => (
                  <span key={t} className="more-tag">{t}</span>
                ))}
              </div>
            </div>
            <Link to="/register" className="btn" style={{ background: '#fff', color: 'var(--blue)', fontWeight: 700, flexShrink: 0 }}>جرّب مجاناً الآن</Link>
          </div>
        </div>
      </section>

      {/* ════ GROWTH ════ */}
      <section className="growth-section">
        <div className="sec-inner">
          <div className="sec-head text-center" data-reveal="up">
            <h2 className="section-title section-title-anim">استراتيجية النمو</h2>
            <p className="section-sub">خطتنا لجذب المدرسين والطلاب وتنمية المنصة</p>
          </div>
          <div className="grid-3">
            {[
              { n: 1, t: 'فيديوهات TikTok و Reels', d: 'فيديوهات تعليمية قصيرة على السوشيال ميديا' },
              { n: 2, t: 'أول درس مجاناً',           d: 'كل طالب جديد يشوف أول درس مجاناً قبل الاشتراك' },
              { n: 3, t: 'نظام الإحالات',             d: 'كل طالب يدعو صديق ويكسب امتيازات' },
              { n: 4, t: 'ترتيب حسب التقييمات',       d: 'المدرسين الأعلى تقييماً يظهروا في أوائل نتائج البحث' },
              { n: 5, t: 'مدرّس الأسبوع',             d: 'تسليط الضوء على مدرّس متميز كل أسبوع' },
              { n: 6, t: 'حجز بالأسبقية',             d: 'أول 50 مدرّس شركاء مؤسسون بمزايا خاصة' },
            ].map((g, i) => (
              <div key={g.n} className="growth-card card-anim card-shimmer" data-reveal="up" data-delay={`${100 + i * 70}`}>
                <div className="growth-num">{g.n}</div>
                <h3>{g.t}</h3>
                <p>{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ PURCHASE NOTIFICATION ════ */}
      {lastPurchase && (
        <Link
          to={`/courses/${lastPurchase.slug || lastPurchase.id}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            className="purchase-notif"
          >
            <div className="pn-avatar">👤</div>
            <div className="pn-text">
              <strong>{lastPurchase.name}</strong> اشترك الآن في <br/>
              <span>{lastPurchase.course}</span>
            </div>
          </motion.div>
        </Link>
      )}

      {/* ════ FLOATING WHATSAPP ════ */}
      <a
        href="https://wa.me/201013201998"
        target="_blank"
        rel="noreferrer"
        className="floating-whatsapp"
        title="تواصل معنا عبر واتساب"
      >
        <span style={{ fontSize: 24 }}>💬</span>
      </a>

      {/* ════ FAQ — FIXED ════ */}
      <section className="faq-section" id="faq">
        <div className="sec-inner">
          <div className="sec-head text-center" data-reveal="up">
            <h2 className="section-title section-title-anim">الأسئلة الشائعة</h2>
            <p className="section-sub">إجابات على أكثر الأسئلة شيوعاً</p>
          </div>
          <div className="faq-list" data-reveal="up" data-delay="100">
            {getDynamicFaqs(liveStats.commission || 15).map((f, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="faq-q" onClick={() => toggleFaq(i)}>
                  <span>{f.q}</span>
                  <span className={`faq-icon-btn ${openFaq === i ? 'faq-icon-open' : ''}`}>
                    {openFaq === i ? '−' : '+'}
                  </span>
                </button>
                {/* Answer — rendered conditionally, no max-height tricks */}
                {openFaq === i && (
                  <div className="faq-a">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ PROMOTIONAL SECTION ════ */}
      <section className="promo-section" data-reveal="up" style={{ paddingBottom: '80px' }}>
        <div className="sec-inner">
          <div className="promo-card">
            <div className="promo-badge">جديد ⚡</div>
            <h2>تطبيق <span className="hero-blue">مدرسك</span> للموبايل قريباً!</h2>
            <p>تابع دروسك، حمل الملخصات، واحضر اللايف من موبايلك في أي وقت وأي مكان.</p>
            <div className="promo-stores">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="App Store" />
            </div>
          </div>
        </div>
      </section>

    </motion.div>
  );
}
